import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { ApiResponse, ApiError } from '../types';

// Default base URL - can be overridden by the consuming application
// Use relative URL for browser environments (works with Vite proxy)
let API_BASE_URL = typeof window !== 'undefined' ? '/api' : 'http://localhost:3000/api';

// Try to get from Vite environment (web)
try {
  // @ts-expect-error import.meta.env is Vite-specific
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) {
    // @ts-expect-error import.meta.env is Vite-specific
    API_BASE_URL = import.meta.env.VITE_API_URL;
  }
} catch {
  // Not in Vite environment, use default
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Function to update base URL (call this from consuming app)
export function setApiBaseUrl(url: string) {
  API_BASE_URL = url;
  apiClient.defaults.baseURL = url;
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API request wrapper
export async function apiRequest<T>(
  config: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.request<ApiResponse<T>>(config);
  return response.data.data;
}

// Pull the server's own error message out of a failed request. Axios only gives
// "Request failed with status code 422", which tells the user nothing.
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as AxiosError<ApiError>)?.response?.data as
    | { error?: string; message?: string; errors?: string[] }
    | undefined;

  return (
    data?.error ||
    data?.message ||
    (Array.isArray(data?.errors) ? data.errors.join(', ') : undefined) ||
    fallback
  );
}

// Auth API
export const authApi = {
  requestOTP: (data: { phone?: string; email?: string }) =>
    apiRequest<{ message: string }>({
      method: 'POST',
      url: '/auth/request_otp',
      data,
    }),

  verifyOTP: (data: { phone?: string; email?: string; code: string }) =>
    apiRequest<{ token: string; user: unknown }>({
      method: 'POST',
      url: '/auth/verify_otp',
      data,
    }),

  logout: () =>
    apiRequest<{ message: string }>({
      method: 'DELETE',
      url: '/auth/logout',
    }),

  me: () =>
    apiRequest<{ user: unknown }>({
      method: 'GET',
      url: '/auth/me',
    }),
};

// Garage Dashboard API
export const garageApi = {
  getDashboard: () =>
    apiRequest<import('../types').GarageDashboardStats>({
      method: 'GET',
      url: '/garage/dashboard',
    }),

  getRequests: (params?: { status?: string; page?: number; per_page?: number }) =>
    apiClient.get('/garage/requests', { params }).then((response) => {
      // Handle paginated response
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  getRequest: (id: string) =>
    apiRequest<unknown>({
      method: 'GET',
      url: `/garage/requests/${id}`,
    }),

  getQuotes: (params?: { status?: string; page?: number; per_page?: number }) =>
    apiClient.get('/garage/quotes', { params }).then((response) => {
      // Handle paginated response
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  submitQuote: (requestId: string, data: { price: number; message?: string; proposed_time_slots?: string[]; line_items?: { description: string; quantity: number; unit_price: number; unit_type: string }[] }) =>
    apiRequest<unknown>({
      method: 'POST',
      url: '/garage/quotes',
      data: { ...data, inspection_request_id: requestId },
    }),

  updateQuote: (id: string, data: Partial<{ price: number; message: string; proposed_time_slots: string[]; line_items?: { description: string; quantity: number; unit_price: number; unit_type: string }[] }>) =>
    apiRequest<unknown>({
      method: 'PUT',
      url: `/garage/quotes/${id}`,
      data,
    }),

  getBookings: (params?: { status?: string; from?: string; to?: string }) =>
    apiClient.get('/garage/bookings', { params }).then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  // Staff booking a customer who phoned in.
  createBooking: (data: {
    service_id: string;
    scheduled_at: string;
    reg_number: string;
    notes?: string;
    customer: { name: string; phone: string; email?: string };
  }) =>
    apiClient.post('/garage/bookings', data).then((response) => response.data.data),

  getBooking: (id: string) =>
    apiClient.get(`/garage/bookings/${id}`).then((response) => {
      if (response.data && response.data.success) return response.data.data;
      return null;
    }),

  updateBooking: (id: string, data: { scheduled_at?: string; status?: string; work_notes?: string; cancellation_reason?: string }) =>
    apiClient.put(`/garage/bookings/${id}`, data).then((response) => response.data.data),

  // The job page owns the work order once a job is booked — for a quote job it
  // writes through to the quote the customer accepted.
  updateJobLineItems: (
    id: string,
    line_items: { description: string; quantity: number; unit_price: number; unit_type: string }[]
  ) =>
    apiClient.put(`/garage/bookings/${id}/line_items`, { line_items }).then((response) => response.data.data),

  uploadJobPhotos: (id: string, photos: File[]) => {
    const formData = new FormData();
    photos.forEach((file) => formData.append('photos[]', file));
    return apiClient.post(`/garage/bookings/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  approveCancellation: (id: string) =>
    apiClient.post(`/garage/bookings/${id}/approve_cancellation`).then((response) => response.data.data),

  // Efterkontroll: offer to drive the car to the re-inspection, then report it back.
  offerAftercheck: (id: string) =>
    apiClient.post(`/garage/bookings/${id}/offer_aftercheck`).then((response) => response.data.data),

  completeAftercheck: (id: string) =>
    apiClient.post(`/garage/bookings/${id}/complete_aftercheck`).then((response) => response.data.data),

  getStaff: () =>
    apiClient.get('/garage/staff').then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  inviteStaff: (data: { email: string; role: string }) =>
    apiRequest<unknown>({
      method: 'POST',
      url: '/garage/staff',
      data,
    }),

  removeStaff: (userId: string) =>
    apiRequest<unknown>({
      method: 'DELETE',
      url: `/garage/staff/${userId}`,
    }),

  updateProfile: (data: Partial<import('../types').BusinessProfile>) =>
    apiRequest<import('../types').BusinessProfile>({
      method: 'PUT',
      url: '/garage/profile',
      data,
    }),

  getProfile: () =>
    apiRequest<import('../types').BusinessProfile>({
      method: 'GET',
      url: '/garage/profile',
    }),

  uploadLogo: (logo: File) => {
    const formData = new FormData();
    formData.append('logo', logo);
    return apiClient.put('/garage/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data as import('../types').BusinessProfile);
  },

  // Services
  getServices: (params?: { category?: string; active?: boolean }) =>
    apiClient.get('/garage/services', { params }).then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  getService: (id: string) =>
    apiClient.get(`/garage/services/${id}`).then((response) => {
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return null;
    }),

  createService: (data: { name: string; description?: string; price?: number | null; category?: string; duration_minutes?: number | null; active?: boolean; image?: File | null }) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    if (data.price !== undefined && data.price !== null) formData.append('price', data.price.toString());
    if (data.category) formData.append('category', data.category);
    if (data.duration_minutes !== undefined && data.duration_minutes !== null) formData.append('duration_minutes', data.duration_minutes.toString());
    formData.append('active', (data.active !== false).toString());
    if (data.image) formData.append('image', data.image);

    return apiClient.post('/garage/services', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  updateService: (id: string, data: { name?: string; description?: string; price?: number | null; category?: string; duration_minutes?: number | null; active?: boolean; image?: File | null }) => {
    const formData = new FormData();
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.price !== undefined && data.price !== null) formData.append('price', data.price.toString());
    if (data.category !== undefined) formData.append('category', data.category);
    if (data.duration_minutes !== undefined && data.duration_minutes !== null) formData.append('duration_minutes', data.duration_minutes.toString());
    if (data.active !== undefined) formData.append('active', data.active.toString());
    if (data.image) formData.append('image', data.image);

    return apiClient.put(`/garage/services/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  deleteService: (id: string) =>
    apiClient.delete(`/garage/services/${id}`).then((response) => response.data),

  // Ads
  getAds: () =>
    apiClient.get('/garage/ads').then((r) => r.data as import('../types').Ad[]),

  createAd: (data: { title: string; description?: string; cta_text?: string; cta_url?: string; template_key: string; start_date: string; end_date: string; active?: boolean; priority?: number }) =>
    apiClient.post('/garage/ads', data).then((r) => r.data as import('../types').Ad),

  updateAd: (id: number, data: Partial<{ title: string; description: string; cta_text: string; cta_url: string; template_key: string; start_date: string; end_date: string; active: boolean; priority: number }>) =>
    apiClient.put(`/garage/ads/${id}`, data).then((r) => r.data as import('../types').Ad),

  deleteAd: (id: number) =>
    apiClient.delete(`/garage/ads/${id}`).then((r) => r.data),

  getClosures: () =>
    apiClient.get('/garage/closures').then((response) => {
      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data as { id: number; closed_on: string }[];
      }
      return [];
    }),

  createClosure: (closed_on: string) =>
    apiClient.post('/garage/closures', { closed_on }).then((response) => response.data.data),

  deleteClosure: (id: number) =>
    apiClient.delete(`/garage/closures/${id}`).then((response) => response.data),

  // Reviews
  getReviews: (params?: { page?: number; perPage?: number }): Promise<import('../types').ReviewsResponse> =>
    apiClient.get('/garage/reviews', { params: { page: params?.page, per_page: params?.perPage } }).then((response) => ({
      reviews: (response.data?.data ?? []) as import('../types').Review[],
      average_rating: Number(response.data?.meta?.average_rating ?? 0),
      review_count: Number(response.data?.meta?.review_count ?? 0),
      current_page: Number(response.data?.meta?.current_page ?? 1),
      has_more: Boolean(response.data?.meta?.has_more),
    })),

  replyToReview: (id: number, reply: string): Promise<import('../types').Review> =>
    apiClient.post(`/garage/reviews/${id}/reply`, { reply }).then((response) => response.data.data),
};

export const carCareApi = {
  // Dashboard
  getDashboard: () =>
    apiRequest<import('../types').CarCareDashboardStats>({
      method: 'GET',
      url: '/car_care/dashboard',
    }),

  // Profile
  getProfile: () =>
    apiRequest<import('../types').BusinessProfile>({
      method: 'GET',
      url: '/car_care/profile',
    }),

  updateProfile: (data: Partial<import('../types').BusinessProfile>) =>
    apiRequest<import('../types').BusinessProfile>({
      method: 'PUT',
      url: '/car_care/profile',
      data,
    }),

  uploadLogo: (logo: File) => {
    const formData = new FormData();
    formData.append('logo', logo);
    return apiClient.put('/car_care/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data as import('../types').BusinessProfile);
  },

  // Bookings
  getBookings: (params?: { status?: string; from?: string; to?: string }) =>
    apiClient.get('/car_care/bookings', { params }).then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  getBooking: (id: string) =>
    apiRequest<unknown>({
      method: 'GET',
      url: `/car_care/bookings/${id}`,
    }),

  updateBooking: (id: string, data: { status?: string; work_notes?: string; cancellation_reason?: string }) =>
    apiClient.put(`/car_care/bookings/${id}`, data).then((response) => response.data.data),

  uploadJobPhotos: (id: string, photos: File[]) => {
    const formData = new FormData();
    photos.forEach((file) => formData.append('photos[]', file));
    return apiClient.post(`/car_care/bookings/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  approveCancellation: (id: string) =>
    apiClient.post(`/car_care/bookings/${id}/approve_cancellation`).then((response) => response.data.data),

  // Services
  getServices: (params?: { category?: string; active?: boolean }) =>
    apiClient.get('/car_care/services', { params }).then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  getService: (id: string) =>
    apiClient.get(`/car_care/services/${id}`).then((response) => {
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return null;
    }),

  createService: (data: { name: string; description?: string; price?: number | null; category?: string; duration_minutes?: number | null; active?: boolean; image?: File | null }) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    if (data.price !== undefined && data.price !== null) formData.append('price', data.price.toString());
    if (data.category) formData.append('category', data.category);
    if (data.duration_minutes !== undefined && data.duration_minutes !== null) formData.append('duration_minutes', data.duration_minutes.toString());
    formData.append('active', (data.active !== false).toString());
    if (data.image) formData.append('image', data.image);

    return apiClient.post('/car_care/services', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  updateService: (id: string, data: Partial<{ name: string; description: string; price: number | null; category: string; duration_minutes: number | null; active: boolean; image: File | null }>) => {
    const formData = new FormData();
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.price !== undefined && data.price !== null) formData.append('price', data.price.toString());
    if (data.category !== undefined) formData.append('category', data.category);
    if (data.duration_minutes !== undefined && data.duration_minutes !== null) formData.append('duration_minutes', data.duration_minutes.toString());
    if (data.active !== undefined) formData.append('active', data.active.toString());
    if (data.image) formData.append('image', data.image);

    return apiClient.put(`/car_care/services/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => response.data.data);
  },

  deleteService: (id: string) =>
    apiRequest<unknown>({
      method: 'DELETE',
      url: `/car_care/services/${id}`,
    }),

  // Staff
  getStaff: () =>
    apiClient.get('/car_care/staff').then((response) => {
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    }),

  // Ads
  getAds: () =>
    apiClient.get('/car_care/ads').then((r) => r.data as import('../types').Ad[]),

  createAd: (data: { title: string; description?: string; cta_text?: string; cta_url?: string; template_key: string; start_date: string; end_date: string; active?: boolean; priority?: number }) =>
    apiClient.post('/car_care/ads', data).then((r) => r.data as import('../types').Ad),

  updateAd: (id: number, data: Partial<{ title: string; description: string; cta_text: string; cta_url: string; template_key: string; start_date: string; end_date: string; active: boolean; priority: number }>) =>
    apiClient.put(`/car_care/ads/${id}`, data).then((r) => r.data as import('../types').Ad),

  deleteAd: (id: number) =>
    apiClient.delete(`/car_care/ads/${id}`).then((r) => r.data),

  getClosures: () =>
    apiClient.get('/car_care/closures').then((response) => {
      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data as { id: number; closed_on: string }[];
      }
      return [];
    }),

  createClosure: (closed_on: string) =>
    apiClient.post('/car_care/closures', { closed_on }).then((response) => response.data.data),

  deleteClosure: (id: number) =>
    apiClient.delete(`/car_care/closures/${id}`).then((response) => response.data),

  // Reviews
  getReviews: (params?: { page?: number; perPage?: number }): Promise<import('../types').ReviewsResponse> =>
    apiClient.get('/car_care/reviews', { params: { page: params?.page, per_page: params?.perPage } }).then((response) => ({
      reviews: (response.data?.data ?? []) as import('../types').Review[],
      average_rating: Number(response.data?.meta?.average_rating ?? 0),
      review_count: Number(response.data?.meta?.review_count ?? 0),
      current_page: Number(response.data?.meta?.current_page ?? 1),
      has_more: Boolean(response.data?.meta?.has_more),
    })),

  replyToReview: (id: number, reply: string): Promise<import('../types').Review> =>
    apiClient.post(`/car_care/reviews/${id}/reply`, { reply }).then((response) => response.data.data),
};

export const adminApi = {
  getAds: () =>
    apiClient.get('/admin/ads').then((r) => r.data as import('../types').Ad[]),

  createAd: (data: { title: string; description?: string; cta_text?: string; cta_url?: string; template_key: string; target_kommun?: string; start_date: string; end_date: string; active?: boolean; priority?: number }) =>
    apiClient.post('/admin/ads', data).then((r) => r.data as import('../types').Ad),

  updateAd: (id: number, data: Partial<{ title: string; description: string; cta_text: string; cta_url: string; template_key: string; target_kommun: string; start_date: string; end_date: string; active: boolean; priority: number }>) =>
    apiClient.put(`/admin/ads/${id}`, data).then((r) => r.data as import('../types').Ad),

  deleteAd: (id: number) =>
    apiClient.delete(`/admin/ads/${id}`).then((r) => r.data),
};

// Export configured client for custom requests
export default apiClient;
